import { CheckOutlined } from '@ant-design/icons';
import { Space, Typography } from 'antd';
import React, { FC, useEffect, useState } from 'react';
import { FileInfo } from '../../../generated';
import FilePreview from '../file-preview/file-preview';
import { AuthResult, AzureBlobResponse } from '../file-upload/azure-upload';
import { FileUploadButton } from '../file-upload/file-upload-button';

const { Title } = Typography;

interface FileUploadButtonWithPreviewProps {
  title?: string;
  description?: string; // text below the title (optional)
  buttonText?: string; // text on the upload button
  permittedContentTypes?: string[];
  permittedExtensions?: string[];
  onAuthorizeRequest: (file: File) => Promise<AuthResult>; // called before uploading or when previewing a file
  fetchFilePreviewData: () => Promise<FileInfo>;
  onUploadSuccess: (uploadedData: AzureBlobResponse) => Promise<void>;
  isPreviewOnly?: boolean;
  setIsEditing?: React.Dispatch<React.SetStateAction<boolean>>;
  isShowFileName?: boolean;
  cropperProps?: object;
  cropAllowed?: boolean; // whether to allow cropping of the file
}
export const FileUploadButtonWithPreview: FC<FileUploadButtonWithPreviewProps> = ({ buttonText = 'Upload File', cropAllowed=true, ...props }) => {
  const previewOnly = props.isPreviewOnly ?? false;
  const [previewFileData, setPreviewFileData] = useState<FileInfo | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasNewFileUploaded, setHasNewFileUploaded] = useState(false);
  useEffect(() => {
    const doGetFilePreviewData = async () => {
      setIsProcessing(true);
      await getFilePreviewData();
      setIsProcessing(false);
    };
    doGetFilePreviewData();
  }, []);

  const getFilePreviewData = async () => {
    const fileData = await props.fetchFilePreviewData();
    setPreviewFileData(fileData);
    setIsProcessing(false);
  };

  const handleAuthorizeRequest = async (file: File): Promise<AuthResult> => {
    setIsProcessing(true);
    setHasNewFileUploaded(false);
    return props.onAuthorizeRequest(file);
  };

  const handleOnError = () => {
    setIsProcessing(false);
  };

  const handleOnUploadSuccess = async (response: AzureBlobResponse) => {
    await getFilePreviewData();
    if (props.setIsEditing) {
      props.setIsEditing(!previewOnly);
    }
    setHasNewFileUploaded(true);

    await props.onUploadSuccess(response);
  };

  return (
    <div
      style={{
        fontSize: '10px'
      }}
    >
      {props.title && <Title level={5}>{props.title}</Title>}
      {props.description && (
        <div
          style={{
            marginBottom: '10px'
          }}
        >
          {props.description}
        </div>
      )}
      <Space direction="horizontal" style={{ alignItems: 'baseline' }}>
        <FilePreview
          fileUrl={previewFileData?.url ?? ''}
          fileName={previewFileData?.name ?? ''}
          fileType={previewFileData?.type ?? ''}
          isProcessing={isProcessing}
          isShowFileName={props.isShowFileName}
        />
        {hasNewFileUploaded && <CheckOutlined style={{ color: '#52c41a', paddingLeft: '5px' }} />}
      </Space>
      {!previewOnly && (
       <FileUploadButton
          isProcessing={isProcessing}
          authorizeRequest={handleAuthorizeRequest}
          buttonText={buttonText}
          maxFileCount={1}
          permittedContentTypes={props.permittedContentTypes}
          permittedExtensions={props.permittedExtensions}
          maxFileSizeBytes={10 * 1024 * 1024} // 10MB, default for pdf
          maxWidthOrHeight={2048}
          onSuccess={handleOnUploadSuccess}
          onError={handleOnError}
          setIsEditing={props.setIsEditing}
          cropperProps={props.cropperProps}
          cropAllowed={cropAllowed}
        />
      )}
    </div>
  );
};
